import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  useNotificationsEmitter,
  useRegisterNotificationCategory,
} from "$global-providers/notifications-provider";
import { RxDBContext } from "$global-providers/rxdb-provider/provider";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useLazyUser } from "$global-providers/user-provider";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { RXDB_PRICE_COLLECTION_NAME } from "../collections/price";
import { RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME } from "../collections/price-list-facility";
import { RXDB_PRICE_LIST_ITEM_COLLECTION_NAME } from "../collections/price-list-item";
import { RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME } from "../collections/price-list-item-external-reference";
import { RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME } from "../collections/price-list-item-name";
import { RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME } from "../collections/price-list-revenue-department";
import { RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME } from "../collections/price-list-tariff";

export const PRICE_LIST_SYNC_CATEGORY = {
  categoryKey: "billing.price-list-sync",
  label: "Price list freshness",
  description: "When local prices haven't synced in over a day",
} as const;

export const PRICE_LIST_SYNC_NOTIFICATION_KEY = "billing.price-list-sync";

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const STALENESS_RECHECK_INTERVAL_MS = 5 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

type DesiredState =
  | { kind: "silent" }
  | { kind: "never-synced" }
  | { kind: "stale"; oldest: string; days: number };

const stateSignature = (state: DesiredState): string => {
  switch (state.kind) {
    case "silent":
      return "silent";
    case "never-synced":
      return "never-synced";
    case "stale":
      return "stale";
  }
};

/**
 * Notification adapter that surfaces price-list freshness in the bell drawer.
 *
 * Replaces the now-deleted `PriceListSyncIndicator` (PNE-409). The original
 * banner read from the `sync-checkpoint` RxDB collection, but the seven
 * price-list replications are global and bypass `createPersistedCheckpointPullBuilders`
 * — they only ever write `lastPulledAt-<collectionName>` to localStorage via
 * `useGqlReplication`. The banner therefore always read null and showed
 * "never synced" even with a populated local DB.
 *
 * This adapter reads from the canonical source (localStorage) and emits
 * a single INFO notification when the oldest of the seven collections
 * is null (never synced) or older than 24h. Recalls when fresh again.
 */
export const useNotificationsAdapterPriceListSync = () => {
  const { emit, recall } = useNotificationsEmitter();
  const { getCurrentTime } = useServerTime();
  const userId = useLazyUser()?.id ?? null;
  const rxdbReady = Boolean(useContext(RxDBContext)?.db);
  const [adapterEnabled, { loading: adapterFlagLoading }] =
    useFeatureFlag<boolean>(featureFlags.notifications.center);
  const [priceListRxdbEnabled, { loading: priceListRxdbFlagLoading }] =
    useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);
  useRegisterNotificationCategory(PRICE_LIST_SYNC_CATEGORY);

  // One useLocalStorage per collection — unrolled rather than mapped so
  // rules-of-hooks is satisfied and call order is statically obvious.
  // The producer (`useGqlReplication`) writes these keys with JSON
  // serialization, so the same hook on the consumer side both decodes
  // them and subscribes to the in-page `local-storage` custom event the
  // producer fires on every write.
  const [pulledFacility] = useLocalStorage<string | null>(
    `lastPulledAt-${RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME}`,
    null,
  );
  const [pulledItem] = useLocalStorage<string | null>(
    `lastPulledAt-${RXDB_PRICE_LIST_ITEM_COLLECTION_NAME}`,
    null,
  );
  const [pulledItemName] = useLocalStorage<string | null>(
    `lastPulledAt-${RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME}`,
    null,
  );
  const [pulledTariff] = useLocalStorage<string | null>(
    `lastPulledAt-${RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME}`,
    null,
  );
  const [pulledItemExternalReference] = useLocalStorage<string | null>(
    `lastPulledAt-${RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME}`,
    null,
  );
  const [pulledRevenueDepartment] = useLocalStorage<string | null>(
    `lastPulledAt-${RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME}`,
    null,
  );
  const [pulledPrice] = useLocalStorage<string | null>(
    `lastPulledAt-${RXDB_PRICE_COLLECTION_NAME}`,
    null,
  );

  // Without this tick, a 23h-old sync would only trip the staleness
  // threshold the next time some other input changes.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setTick((t) => t + 1),
      STALENESS_RECHECK_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, []);

  const flagsLoading = adapterFlagLoading || priceListRxdbFlagLoading;
  const gated = !flagsLoading && (!adapterEnabled || !priceListRxdbEnabled);

  // Advanced only once the write resolves — `emit`/`recall` no-op until userId
  // and the RxDB collection exist, and treating a no-op as applied is what
  // stranded a row that never got recalled.
  const appliedSignatureRef = useRef<string | null>(null);
  const inFlightSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    // `tick` is read so React re-runs this effect on the staleness re-check
    // interval even when nothing else changed.
    void tick;
    if (flagsLoading) return;

    const desired: DesiredState = (() => {
      if (gated) return { kind: "silent" };
      const pulledAts = [
        pulledFacility,
        pulledItem,
        pulledItemName,
        pulledTariff,
        pulledItemExternalReference,
        pulledRevenueDepartment,
        pulledPrice,
      ];
      if (pulledAts.some((v) => v === null)) {
        return { kind: "never-synced" };
      }
      const oldest = pulledAts.reduce((min, v) =>
        (v as string) < (min as string) ? v : min,
      ) as string;
      const oldestMs = new Date(oldest).getTime();
      // `getCurrentTime()` throws "Server time not yet known" before the first
      // server-time exchange settles. Falling back to `Date.now()` keeps the
      // adapter safe during initial app boot — staleness is a >24h signal so
      // a few seconds of clock drift either way is fine here. Same trade-off
      // the notifications provider makes for its render-time UX filtering.
      let nowMs: number;
      try {
        nowMs = getCurrentTime().getTime();
      } catch {
        nowMs = Date.now();
      }
      const ageMs = nowMs - oldestMs;
      // Malformed timestamp in localStorage → NaN comparisons all return false,
      // which would otherwise fall through to the emit path with "NaN days".
      // Treat as "never synced" instead.
      if (!Number.isFinite(ageMs)) return { kind: "never-synced" };
      if (ageMs <= STALE_THRESHOLD_MS) return { kind: "silent" };
      return { kind: "stale", oldest, days: Math.floor(ageMs / DAY_MS) };
    })();

    const signature = stateSignature(desired);

    if (!userId || !rxdbReady) return;
    if (signature === appliedSignatureRef.current) return;
    if (signature === inFlightSignatureRef.current) return;

    const key = PRICE_LIST_SYNC_NOTIFICATION_KEY;
    const write =
      desired.kind === "silent"
        ? recall(key)
        : desired.kind === "never-synced"
          ? emit({
              key,
              title: "Price list hasn't synced yet",
              body: "Some prices may be missing until the next sync.",
              severity: "INFO",
              category: PRICE_LIST_SYNC_CATEGORY.categoryKey,
              icon: "💰",
              version: "never-synced",
            })
          : emit({
              key,
              title: "Price list is out of date",
              body: `Local prices haven't synced for ${desired.days} day${desired.days === 1 ? "" : "s"}. Reconnect to refresh.`,
              severity: "INFO",
              category: PRICE_LIST_SYNC_CATEGORY.categoryKey,
              icon: "💰",
              // Version tags the row for dismissal survival; `oldest` at emit
              // time gives one stable tag per staleness episode. It isn't
              // re-emitted if a different collection later becomes the oldest
              // mid-episode — that's deliberate, so a dismissal isn't undone.
              version: desired.oldest,
            });

    inFlightSignatureRef.current = signature;
    write
      .then(() => {
        appliedSignatureRef.current = signature;
      })
      .catch((error) => {
        // Leave appliedSignatureRef untouched → retried on the next tick.
        console.error("price-list-sync notification write failed", {
          signature,
          error,
        });
      })
      .finally(() => {
        if (inFlightSignatureRef.current === signature) {
          inFlightSignatureRef.current = null;
        }
      });
  }, [
    flagsLoading,
    gated,
    tick,
    userId,
    rxdbReady,
    emit,
    recall,
    getCurrentTime,
    pulledFacility,
    pulledItem,
    pulledItemName,
    pulledTariff,
    pulledItemExternalReference,
    pulledRevenueDepartment,
    pulledPrice,
  ]);
};
